import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import { FormInstance } from 'antd/es/form';

import { createCommunity, getCommunity, getCompetitors, updateCommunity } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import { COMPANY_STATUS } from 'constants/company';
import { COMMUNITY_RENTAL_TYPE } from 'constants/rentalTypes';
import { CommunityAdminListType } from 'types/communities';
import { CompanyBaseType } from 'types/company';
import { ContractType } from 'types/contracts';
import { HoldingType } from 'types/holding';

import { COMMUNITY_BASIC_INFO_FIELDS } from './constants';
import ConfirmModal from '../../../common/Modal/ConfirmModal';

type CommunityAdminType = {
  open: boolean;
  loading?: boolean;
  loadingCommunity: boolean;
  loadingCompetitors: boolean;
  form: FormInstance;
  showConformation: boolean;
  communityId?: number;
  community?: CommunityAdminListType;
  selectedCompany?: CompanyBaseType | undefined;
  selectedHoldings?: HoldingType[] | undefined;
  includeInRegularShopCadence: boolean;
  competitors: CommunityAdminListType[];
  setLoading?: Dispatch<SetStateAction<boolean>>;
  setSelectedCompany: Dispatch<SetStateAction<CompanyBaseType | undefined>>;
  setSelectedHoldings: Dispatch<SetStateAction<HoldingType[] | undefined>>;
  setIncludeInRegularShopCadence: Dispatch<SetStateAction<boolean>>;
  setCompetitors: Dispatch<SetStateAction<CommunityAdminListType[]>>;
  setShowConformation: Dispatch<SetStateAction<boolean>>;
  handleConfirm: (values: any) => void;
  handleCancel: () => void;
};

interface Props {
  open: boolean;
  communityId?: number;
  onClose: (refresh?: boolean) => void;
  children: ReactNode;
}

const CommunityAdminContext = createContext<CommunityAdminType | undefined>(undefined);

export const useCommunityAdminContext = () => {
  const context = useContext(CommunityAdminContext);
  if (!context) throw new Error('useCommunityAdminContext must be used within CommunityAdminContext');
  return context;
};

export const CommunityCreateProvider = ({ communityId, open, children, onClose }: Props) => {
  const { showMessage } = useRootContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyBaseType | undefined>(undefined);
  const [selectedHoldings, setSelectedHoldings] = useState<HoldingType[] | undefined>(undefined);
  const [includeInRegularShopCadence, setIncludeInRegularShopCadence] = useState<boolean>(true);
  const [competitors, setCompetitors] = useState<CommunityAdminListType[]>([]);
  const [showConformation, setShowConformation] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);

  const [form] = Form.useForm();

  const {
    data: community,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['community', 'basic', 'info', communityId],
    queryFn: () => getCommunity(communityId as number),
    onSuccess: (data: CommunityAdminListType) => {
      setSelectedCompany(data.company);
      setIncludeInRegularShopCadence(
        data.company?.status === COMPANY_STATUS.PAYING_CUSTOMER ? data.include_in_regular_shop_cadence ?? true : false
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(communityId),
  });

  const { isLoading: isCompetitorsLoading, isFetching: isCompetitorsFetching } = useQuery({
    queryKey: ['competitors', communityId],
    queryFn: () => getCompetitors(communityId as number),
    onSuccess: (data: CommunityAdminListType[]) => {
      setCompetitors(data);
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(communityId),
  });

  const handleConfirm = async (values: any) => {
    const dataFieldsTouched = form.isFieldsTouched(COMMUNITY_BASIC_INFO_FIELDS);
    if (community && dataFieldsTouched) {
      setData(values);
      setShowConformation(true);
    } else {
      await handleSubmit(values);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const competitors_ids = (competitors ?? []).map((competitor) => competitor.id);

    const lifePlanContracts = Object.values((values?.contracts ?? {}) as Record<string, ContractType[]>)
      .flatMap((items: ContractType[]) => items)
      .filter((item: ContractType) => Boolean(item) && item.type);

    const rentalTypes: number[] = values.rental_types || [];
    const hasLifePlanRentalType = rentalTypes.includes(COMMUNITY_RENTAL_TYPE.CCRC);
    const contracts = hasLifePlanRentalType ? lifePlanContracts : [];

    const payload = {
      ...values,
      rental_types: rentalTypes,
      company_id: selectedCompany?.id,
      holding_ids: selectedHoldings?.length ? selectedHoldings.map((holding) => holding.id) : undefined,
      include_in_regular_shop_cadence: includeInRegularShopCadence,
      competitors: competitors_ids,
      contracts,
    };

    await (communityId ? updateCommunity(communityId, payload) : createCommunity(payload))
      .then(() => {
        showMessage('success', `Community ${communityId ? 'updated' : 'created'} successfully.`);
        setShowConformation(false);
        setData(null);
        onClose(true);
      })
      .catch((err: Error) => {
        showMessage('error', `Failed to create community.`);
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = () => {
    setCompetitors([]);
    setSelectedCompany(undefined);
    setIncludeInRegularShopCadence(true);
    setShowConformation(false);
    setData(null);
    form.resetFields();
    onClose();
  };

  return (
    <CommunityAdminContext.Provider
      value={{
        open,
        loading,
        loadingCommunity: isFetching || isLoading,
        loadingCompetitors: isCompetitorsLoading || isCompetitorsFetching,
        form,
        communityId,
        community,
        selectedCompany,
        selectedHoldings,
        includeInRegularShopCadence,
        showConformation,
        competitors,
        setLoading,
        setSelectedCompany,
        setSelectedHoldings,
        setIncludeInRegularShopCadence,
        setCompetitors,
        setShowConformation,
        handleConfirm,
        handleCancel,
      }}
    >
      <ConfirmModal
        open={showConformation}
        subtitle={
          <a
            href={`communities/${community?.id}/info`}
            style={{ color: 'var(--dark-blue)' }}
            target={'_blank'}
            rel="noreferrer"
          >
            {community?.comunity_name}
          </a>
        }
        title={'Data Propagation Notice'}
        description={
          'You are about to save system-level changes that will affect displayed data for multiple companies and communities. Are you sure you want to do this?'
        }
        loading={loading}
        disabled={loading}
        checkboxConfirmLabel={'I understand that this will affect other companies and communities'}
        confirmLabel="Confirm & Save"
        onConfirm={() => handleSubmit(data)}
        onCancel={() => setShowConformation(false)}
      />
      {children}
    </CommunityAdminContext.Provider>
  );
};
